import { Injectable } from '@angular/core';
import { Sample } from 'src/app/models/sample.model';
import { FormField } from 'src/app/shared/form/formField.model';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { Units, UnitSymbols } from 'src/app/enums/units.enum';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { isCustomFieldKey } from 'src/app/shared/form/custom-field-helpers';
interface OrgLevelOption { id: string; name: string; sampleNumberTemplate?: string; }

@Injectable()
export class SampleFormFieldBuilderService {

  constructor(
    private valueConversionService: ValueConversionService
  ) { }

  /**
   * Get helper method to apply section visibility
   */
  getSectionVisibility() {
    return (fields: FormField[], visibility: (sample: Sample) => boolean): FormField[] => {
      return fields.map(field => ({
        ...field,
        isVisible: (sample) => visibility(sample) && (field.isVisible?.(sample) ?? true)
      }));
    };
  }

  /**
   * Update flow rate field labels based on sample type
   */
  updateFlowFields(entity: Sample, fields: FormField[]): void {
    const soundUnit = UnitSymbols[Units.Db];
    const flowRateUnit = this.valueConversionService.getFlowRateUnit();
    const isNoise = entity?.sampleType && entity?.sampleType === SampleTypeEnum.Noise;

    fields?.forEach(field => {
      const unit = '(' + (isNoise ? soundUnit : flowRateUnit) + ')';
      if (field.property == nameOf<Sample>(x => x.samplePreCal)) {
        field.name = isNoise ? 'Pre Calibration Value' : 'Initial Flow Rate';
        field.additionalLabel = () => unit;
      } else if (field.property == nameOf<Sample>(x => x.samplePostCal)) {
        field.name = isNoise ? 'Post Calibration Value' : 'Final Flow Rate';
        field.additionalLabel = () => unit;
      } else if (field.property == nameOf<Sample>(x => x.sampleCategory)) {
        if (isNoise) {
          if (entity.sampleCategory === SampleCategoryEnum.Blank) {
            entity.sampleCategory = null;
          }
          field.options = getEnumLookup(SampleCategoryEnum).filter(res => res.id != SampleCategoryEnum.Blank);
        } else {
          field.options = getEnumLookup(SampleCategoryEnum);
        }
      }
    });
  }

  /**
   * Update dimension fields based on length/width/area values
   */
  updateDimensionFields(entity: Sample, fields: FormField[]): void {
    const length = entity['length'] as number | undefined;
    const width = entity['width'] as number | undefined;
    const area = entity['area'] as number | undefined;
    const isAuto = length && width && area === length * width;
    let lengthField: FormField, widthField: FormField, areaField: FormField;

    for (const f of fields) {
      if (f.property === 'length') lengthField = f;
      else if (f.property === 'width') widthField = f;
      else if (f.property === 'area') areaField = f;
    }

    if (!lengthField || !widthField || !areaField) return;

    if (length || width) {
      entity['area'] = length && width ? length * width : null;
      areaField.readonly = true;
    } else if (area && !isAuto && !(length || width)) {
      lengthField.readonly = true;
      widthField.readonly = true;
    } else {
      lengthField.readonly = false;
      widthField.readonly = false;
      areaField.readonly = false;
    }
  }

  /**
   * Update user options in form fields
   */
  updateUserOptions(entity: Sample, organizationLevels: OrgLevelOption[], fields: FormField[]): void {
    fields?.forEach(field => {
      if (field.property === nameOf<Sample>(x => x.organizationLevelId)) {
        const orgLevel = organizationLevels?.find(org => org.id === entity[field.property]);
        entity.organizationLevelName = orgLevel?.name;
      }
    });
  }

  /**
   * Update sample number field based on organization level
   */
  updateSampleNumberField(entity: Sample, organizationLevels: OrgLevelOption[], fields: FormField[]): void {
    const orgLevel = organizationLevels?.find(org => org.id === entity?.organizationLevelId);
    const sampleField = fields.find(field => field.property === nameOf<Sample>(x => x.sampleNumber));
    if (!sampleField) return;

    if (orgLevel?.sampleNumberTemplate) {
      sampleField.readonly = true;
      if (!entity.sampleNumber) {
        entity.sampleNumber = 'Autogenerated';
      }
    } else {
      sampleField.readonly = false;
    }
  }

  /**
   * Apply shift correction field requirements
   */
  applyShiftCorrectionChanges(entity: Sample, fields: FormField[]): void {
    const shiftFieldProperties = [nameOf<Sample>(x => x.shiftLength), nameOf<Sample>(x => x.averageWeeklyHours_Seconds)] as string[];

    if (fields?.length) {
      const shiftFields = fields.filter(field => shiftFieldProperties.includes(field.property));
      shiftFields.forEach(prop => { prop.required = entity.applyShiftCorrection; });
    }
  }

  /**
   * Apply pressure correction field requirements
   */
  applyPressureCorrectionChanges(entity: Sample, fields: FormField[]): void {
    if (!fields?.length) return;
    const property = fields.find(field => field.property === nameOf<Sample>(x => x.pressureMmhg));
    if (!property) return;
    property.required = entity.applyPressureCorrection;
  }

}
